/**
 * GetPlanOverrides — reads the overrides a plan was generated with, straight from
 * the persisted `inputOverrides` object rather than from Redux.
 *
 * Redux only holds the overrides of the plan currently being built, so it is empty
 * when an operator opens a plan they did not just create — a published plan, or a
 * past shift's closed plan. This is how the read-only plan summary page shows what
 * that plan actually carried.
 */
import { OutputAdjustmentEntry } from "../pages/checkout/model";
import { API_GATEWAY_PROCESS_NAME } from "../utils/constants";
import { apiRequest } from "./apiMiddleware";
import { store } from "./../store";
import { selectStationCode } from "../store/slices/shiftPlanSlice";

/**
 * Subset of the GetPlanOverrides response the UI reads today. The operation also
 * returns the rates/category/volume/trailer sections, which the Rates & Roster and
 * Volume & Buffer views already fetch through their own page APIs.
 */
export interface GetPlanOverridesResponse {
  planId?: string;
  /** Per-process-path adjustments made on the Checkout page. */
  outputAdjustments?: OutputAdjustmentEntry[];
}

/**
 * Fetches the persisted overrides for a plan.
 *
 * Resolves to null only when the call succeeded but carried no body — i.e. the plan
 * genuinely has nothing to show. A failed call rethrows, because this operation is the
 * sole source of a plan's checkout overrides: swallowing the error would render a
 * broken fetch as "this plan was published without any adjustments", which is a
 * different and wrong statement about a published plan.
 */
export const getPlanOverrides = async (
  planId: string
): Promise<GetPlanOverridesResponse | null> => {
  const stationCode = selectStationCode(store.getState()) || "";
  try {
    const response = await apiRequest<GetPlanOverridesResponse>({
      resourcePath: "/getPlanOverrides",
      httpMethod: "post",
      processName: API_GATEWAY_PROCESS_NAME,
      requestBody: {
        planId,
        warehouseId: stationCode,
      },
    });
    return response.data ?? null;
  } catch (error) {
    console.error("Error fetching plan overrides for planId:", planId, error);
    throw error;
  }
};
